import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WorkshopDetail from '../../components/Kpathport/Workshop/WorkshopDetail';
import { getWorkshopDetail } from '../../api/workshop/workshop.api';
import { getWorkshopMVPs } from '../../api/workshop/mvp.api';
import { BaseButton, useStaleCheck } from '@repo/common';

const Workshop = () => {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useStaleCheck(workshopId ? `/workshops/${workshopId}` : null);

  const refreshNoticeSection = useCallback(async () => {
    if (!workshopId) return;

    try {
      const data = await getWorkshopDetail(parseInt(workshopId));
      setWorkshop((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          notice: data.notice || '',
        };
      });
    } catch (err) {
      console.error('공지사항 갱신 실패:', err);
    }
  }, [workshopId]);

  const fetchWorkshop = useCallback(async () => {
    if (!workshopId) {
      setError('워크샵 ID가 없습니다.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [data, mvps] = await Promise.all([
        getWorkshopDetail(parseInt(workshopId)),
        getWorkshopMVPs(parseInt(workshopId)),
      ]);

      // 백엔드 응답을 프론트엔드 형식으로 변환
      const transformedData = {
        id: data.id,
        step: data.category || '',
        category: data.category || '',
        title: data.title || '',
        images: data.images || [],
        dateSlots: (data.dateSlots || []).map((slot) => {
          // 날짜 포맷팅: "2025-12-05" -> "Fri ∙ 5 Dec 2025"
          const formatDate = (dateString) => {
            const date = new Date(dateString);
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const months = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec',
            ];
            const dayName = days[date.getDay()];
            const day = date.getDate();
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            return `${dayName} ∙ ${day} ${month} ${year}`;
          };

          // 시간 포맷팅: "18:00:00" -> "18:00 - 20:00PM"
          const formatTime = (startTime, endTime) => {
            const formatSingleTime = (timeString) => {
              if (!timeString) return '';
              const [hours, minutes] = timeString.split(':');
              const hour24 = parseInt(hours, 10);
              const hour12 =
                hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
              const ampm = hour24 >= 12 ? 'PM' : 'AM';
              return `${hour12}:${minutes || '00'}${ampm}`;
            };

            const start = formatSingleTime(startTime);
            const end = formatSingleTime(endTime);
            return start && end ? `${start} - ${end}` : start || end;
          };

            return {
              id: slot.id,
              date: formatDate(slot.date),
              rawDate: slot.date,        // ISO "YYYY-MM-DD" — 과거 날짜 검증용
              time: formatTime(slot.startTime, slot.endTime),
              startTime: slot.startTime,
              endTime: slot.endTime,
              maxParticipants: slot.maxParticipants,
              currentParticipants: slot.currentParticipants,
              available: slot.available,
              dateSlotStatus: slot.status,
              isPast: slot.isPast || false,  // 백엔드 계산 결과
              status: slot.available
                ? (slot.maxParticipants - slot.currentParticipants > 0
                    ? `${slot.maxParticipants - slot.currentParticipants} left`
                    : 'Full')
                : 'Full',
            };
          }),
          location: data.location || '',
          // 숫자 그대로 넘기고 표기는 WorkshopDetail 이 통화를 보고 정한다 (KRW → "55,000won")
          price: data.price ?? (data.free ? 0 : null),
          currency: data.currency || 'USD',
          // 'CARD' | 'BANK_TRANSFER' — 계좌이체 워크숍은 상세 화면에 안내 뱃지를 띄운다
          paymentMethod: data.paymentMethod || 'CARD',
          bankTransfer: data.bankTransfer || null,
          description: data.description || '',
          keywords: data.keywords || [],
          details: data.details || '',
          notice: data.notice || '',
          inquiry: data.inquiryContact || {
            phone: '',
            email: '',
            openChatId: '',
          },
          refundPolicy: data.refundPolicy || '',
          meetingPoint: data.meetingPoint ? {
            name: data.meetingPoint.name || '',
            address: data.meetingPoint.address || '',
            mapImage: data.meetingPoint.mapImage || '',
            placeId: data.meetingPoint.placeId || '',
            latitude: data.meetingPoint.latitude ?? null,
            longitude: data.meetingPoint.longitude ?? null,
          } : {
            name: '',
            address: '',
            mapImage: '',
          },
          mvps: mvps || [],
          thingsToKnow: data.thingsToKnow || '',
          featured: data.featured || false,
          hit: data.hit || false,
          free: data.free || false,
          status: data.status || 'ACTIVE',
          isBlocked: Boolean(data.isBlocked),
          unblockedAt: data.unblockedAt || data.blockedUntil || null,
          onNoticeCreated: refreshNoticeSection,
        };
        
      setWorkshop(transformedData);
    } catch (err) {
      console.error('워크샵 조회 실패:', err);
      setError('워크샵을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [workshopId, refreshNoticeSection]);

  useEffect(() => {
    fetchWorkshop();
  }, [fetchWorkshop]);

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <p>로딩 중...</p>
      </div>
    );
  }

  if (error || !workshop) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen gap-4'>
        <p className='text-red-500'>{error || '워크샵을 찾을 수 없습니다.'}</p>
        <BaseButton
          type='primary'
          size='m'
          hideIcon
          onClick={() => navigate('/community/kpathport/pathfeed')}
          className='h-auto min-w-0 px-4 py-2 text-white rounded border-0 bg-green-deep02'
        >
          목록으로 돌아가기
        </BaseButton>
      </div>
    );
  }

  return <WorkshopDetail workshop={workshop} />;
};

export default Workshop;
